const SID_KEY = 'vb_visitor_sid';
const UTM_KEY = 'vb_visitor_utm';
const HEARTBEAT_MS = 45_000;

let started = false;
let heartbeatTimer = null;
let currentUserId = null;
let lastPathSent = '';
let lastSentAt = 0;

function randomSid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `vb_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 12)}`;
}

function getSid() {
  try {
    let sid = localStorage.getItem(SID_KEY);
    if (!sid || sid.length < 8) {
      sid = randomSid();
      localStorage.setItem(SID_KEY, sid);
    }
    return sid;
  } catch {
    return randomSid();
  }
}

function readUtmFromLocation() {
  try {
    const params = new URLSearchParams(window.location.search);
    const utm = {
      source: params.get('utm_source') || '',
      medium: params.get('utm_medium') || '',
      campaign: params.get('utm_campaign') || '',
      content: params.get('utm_content') || '',
      term: params.get('utm_term') || '',
    };
    if (!utm.source && !utm.medium && !utm.campaign) return null;
    return utm;
  } catch {
    return null;
  }
}

function getStoredUtm() {
  try {
    const raw = localStorage.getItem(UTM_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function captureFirstTouchUtm() {
  const fromUrl = readUtmFromLocation();
  if (!fromUrl) return getStoredUtm();
  try {
    const existing = getStoredUtm();
    if (!existing) {
      localStorage.setItem(UTM_KEY, JSON.stringify(fromUrl));
      return fromUrl;
    }
  } catch {
    /* ignore */
  }
  return fromUrl || getStoredUtm();
}

function currentPath() {
  return `${window.location.pathname || '/'}${window.location.search || ''}`;
}

function buildPayload(path) {
  const utm = captureFirstTouchUtm() || {};
  return {
    sid: getSid(),
    path: path || currentPath(),
    referrer: document.referrer || '',
    utm_source: utm.source || null,
    utm_medium: utm.medium || null,
    utm_campaign: utm.campaign || null,
    utm_content: utm.content || null,
    utm_term: utm.term || null,
    user_id: currentUserId || null,
  };
}

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function postBeaconFetch(body, keepalive, attempt = 0) {
  try {
    const res = await fetch('/api/analytics/beacon', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive,
      credentials: 'same-origin',
    });
    if (res.ok || res.status < 500) return;
    if (attempt < 1 && !keepalive) {
      await sleep(600);
      return postBeaconFetch(body, keepalive, attempt + 1);
    }
  } catch {
    if (attempt < 1 && !keepalive) {
      await sleep(600);
      return postBeaconFetch(body, keepalive, attempt + 1);
    }
  }
}

function postBeacon(payload, { keepalive = false } = {}) {
  const body = JSON.stringify(payload);
  try {
    if (keepalive && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon('/api/analytics/beacon', blob)) return;
    }
  } catch {
    /* fall through */
  }
  void postBeaconFetch(body, keepalive);
}

export function setVisitorUserId(userId) {
  const next = userId != null && Number(userId) > 0 ? Number(userId) : null;
  if (currentUserId === next) return;
  currentUserId = next;
  if (started) sendVisitorBeacon({ force: true });
}

export function sendVisitorBeacon({ force = false, keepalive = false } = {}) {
  if (typeof window === 'undefined') return;
  if (document.visibilityState === 'hidden' && !keepalive && !force) return;

  const path = currentPath();
  const now = Date.now();
  if (!force && path === lastPathSent && now - lastSentAt < 4000) return;

  lastPathSent = path;
  lastSentAt = now;
  postBeacon(buildPayload(path), { keepalive });
}

function onVisibility() {
  if (document.visibilityState === 'visible') sendVisitorBeacon({ force: true });
}

function onPageHide() {
  sendVisitorBeacon({ force: true, keepalive: true });
}

export function startVisitorBeacon() {
  if (typeof window === 'undefined' || started) return;
  started = true;
  captureFirstTouchUtm();
  sendVisitorBeacon({ force: true });

  heartbeatTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') sendVisitorBeacon({ force: true });
  }, HEARTBEAT_MS);

  document.addEventListener('visibilitychange', onVisibility);
  window.addEventListener('pagehide', onPageHide);
}

export function trackVisitorPath() {
  sendVisitorBeacon({ force: true });
}
